import { OrdersResponse, TradesResponse, PositionsResponse } from "./books.interfaces";
import { Quote, SymbolSearchService, PreviousPositionsInterface } from '@shared/providers';
import { getPipeMap } from './books.utils';
import * as _ from 'underscore';
import { DEFAULT_PRECISION, Utils } from '@app/_helpers';
import { BriefStrategyCache } from "../market-place/providers/market-place/market-place.models";




/****************************************************
 ****************************************************
 ************** Models for Orders Book **************
 ****************************************************
 ****************************************************/

export class OrderBook implements OrdersResponse {
    algo_id = '';
    strategy_name = '';
    broker = '';
    broker_order_id = 0;
    strategy_type = null;
    client_order_id = 0;
    exchange_order_id = '';
    exchange_order_time = 0;
    filled_quantity = 0;
    filled_lots = 0;
    instrument = '';
    order_condition = '';
    order_mode = '';
    order_price = '';
    order_quantity = 0;
    order_lots = 0;
    order_status = '';
    order_type = '';
    order_validity = '';
    product_type = '';
    rapid_order_id = '';
    scrip_name = '';
    sequence_number: 0;
    symbol_id = 0;
    error_text = '';
    stop_price = '';
    market_id = '';
    security_id = 0;
    lot_size = 1;
    unique_algo_name = '';
    portfolio_id = '';
    leg_id = '';
    trigger_type = '';
    ucc?: string;
    client_name?: string;

    constructor(orderBook: OrdersResponse, ucc?: string, client_name?: string) {
        const pipeMap = getPipeMap();
        Object.keys(this).forEach(key => {
            try {
                this[key] = pipeMap.hasOwnProperty(key) ? pipeMap[key][orderBook[key]] : orderBook[key];
            } catch (error) {
                console.error('Error in calling transform function in order model.', key, error)
            }
        });

        if(ucc && client_name){
            this.ucc = ucc;
            this.client_name = client_name;
        }

        this.algo_id = this.unique_algo_name;
        this.updateAlgoName();
        this.updateFields();
    }

    updateAlgoName() {
        const portfolio = BriefStrategyCache.Instance.getPortfolioByUniqueID(this.portfolio_id);
        if (portfolio) {
            this.strategy_name = portfolio.portfolioName;
        }
    }

    updateFields() {
        this.exchange_order_time = Utils.convertDateTime(this.exchange_order_time);
        this.filled_lots = this.filled_quantity / this.lot_size;
        this.order_lots = this.order_quantity / this.lot_size;
        this.order_price = Utils.precisionUtil(parseFloat(this.order_price), DEFAULT_PRECISION);
        this.stop_price = Utils.precisionUtil(parseFloat(this.stop_price), DEFAULT_PRECISION);
    }

    updateOrderBook(updatedData: OrdersResponse) {
        const pipeMap = getPipeMap();
        Object.keys(updatedData).forEach(key => {
            try {
                this[key] = pipeMap.hasOwnProperty(key) ? pipeMap[key][updatedData[key]] : updatedData[key];
            } catch (error) {
                console.error('Error in calling transform function in order model.', key, error)
            }
        });
        this.updateFields();
    }
}

/****************************************************
 ****************************************************
 ************** Models for Trades Book **************
 ****************************************************
 ****************************************************/

export class TradeBook implements TradesResponse {
    algo_id = '';
    strategy_name = '';
    broker = '';
    client_order_id = 0;
    exchange_order_id = '';
    exchange_order_time = 0;
    filled_price = 0;
    filled_quantity = 0;
    filled_lots = 0;
    instrument = '';
    order_condition = '';
    order_mode = '';
    order_price = '';
    order_quantity = 0;
    order_lots = 0;
    order_status = '';
    order_type = '';
    order_validity = '';
    product_type = '';
    rapid_order_id = '';
    sequence_number: 0;
    scrip_name = '';
    strategy_type = null;
    trade_time = 0;
    market_id = '';
    security_id = 0;
    lot_size = 1;
    trade_id = '';
    unique_algo_name = '';
    portfolio_id = '';
    leg_id = '';
    trigger_type = '';
    ucc?: string;
    client_name?: string;

    constructor(tradeBook: TradesResponse | PreviousPositionsInterface, ucc?: string, client_name?: string) {
        const pipeMap = getPipeMap();
        
        Object.keys(this).forEach(key => {
            this[key] = pipeMap.hasOwnProperty(key) ? pipeMap[key][tradeBook[key]] : tradeBook[key];
        });
        this.applyPrecision();

        if(ucc && client_name){
            this.ucc = ucc;
            this.client_name = client_name;
        }

        this.algo_id = this.unique_algo_name;
        this.updateAlgoName();
    }

    updateAlgoName() {
        const portfolio = BriefStrategyCache.Instance.getPortfolioByUniqueID(this.portfolio_id);
        if (portfolio) {
            this.strategy_name = portfolio.portfolioName;
        }
    }

    applyPrecision() {
        this.exchange_order_time = Utils.convertDateTime(this.exchange_order_time);
        this.trade_time =  Utils.convertDateTime(this.trade_time);
        this.order_lots = this.order_quantity / this.lot_size;
        this.filled_lots = this.filled_quantity / this.lot_size;
        this.order_price = Utils.precisionUtil(parseFloat(this.order_price), DEFAULT_PRECISION);
        this.filled_price = this.filled_price;
    }

    updateTradeBook(updatedData: TradesResponse) {
        const pipeMap = getPipeMap();
        Object.keys(updatedData).forEach(key => {
            try {
                this[key] = pipeMap.hasOwnProperty(key) ? pipeMap[key][updatedData[key]] : updatedData[key];
            } catch (error) {
                console.error('Error in calling transform function in trade model.', key, error)
            }
        });
        this.applyPrecision();
    }
}


/****************************************************
 ****************************************************
 ************* Models for Positions Book ************
 ****************************************************
 ****************************************************/

export class PositionBook implements PositionsResponse {
    avg_buy_price = '0.00';
    avg_sell_price = '0.00';
    booked_pnl = '0.00';
    broker = '';
    buy_amount = '0.00';
    buy_quantity = 0;
    buy_lots = 0;
    mtm = 0;
    net_qty = 0;
    net_lots = 0;
    net_value = '0.00';
    net_pnl = '0.00'
    position_type = '';
    product = '';
    scrip_name = '';
    sequence_number: 0;
    strategy_type = 0;
    sell_amount = '0.00';
    sell_quantity = 0;
    sell_lots = 0;
    scrip_precision = 0;
    symbol_id = 0;
    ltp = 0;
    market_id = '';
    security_id = 0;
    lot_size = 1;
    target_profit = 0;
    stop_loss = 0;

    constructor(positionBook: PositionsResponse, symbolSearchService: SymbolSearchService) {
        const pipeMap = getPipeMap();
        Object.keys(this).forEach(key => {
            if (positionBook[key] !== null) {
                this[key] = pipeMap.hasOwnProperty(key) ? pipeMap[key][positionBook[key]] : positionBook[key];
            }
        });
        const closeQty = Math.min(this.buy_quantity, this.sell_quantity);
        this.avg_buy_price = Utils.precisionUtil((parseFloat(this.buy_amount) / this.buy_quantity || 0), DEFAULT_PRECISION, false);
        this.avg_sell_price = Utils.precisionUtil((parseFloat(this.sell_amount) / this.sell_quantity || 0), DEFAULT_PRECISION, false);
        this.net_qty = +(this.buy_quantity - this.sell_quantity);
        this.buy_amount = Utils.precisionUtil(parseFloat(this.buy_amount), DEFAULT_PRECISION);
        this.sell_amount = Utils.precisionUtil(parseFloat(this.sell_amount), DEFAULT_PRECISION);
        this.net_value = (parseFloat(this.sell_amount) - parseFloat(this.buy_amount)).toFixed(DEFAULT_PRECISION);
        this.booked_pnl = ((parseFloat(this.avg_sell_price) - parseFloat(this.avg_buy_price)) * closeQty || 0).toFixed(DEFAULT_PRECISION);
        this.buy_lots = this.buy_quantity / this.lot_size;
        this.net_lots = this.net_qty / this.lot_size;
        this.sell_lots = this.sell_quantity / this.lot_size;
    }

    listenLtp(quote: Quote) {
        quote.ltpUpdate.subscribe(() => {
            this.ltp = quote.LTP;
            this.updateMTM();
        });

    }
    // If open_qty > 0, m2m = (last_trade_price - buy_price) * open_qty;
    // Else m2m = (sell_price - last_trade_price) * open_qty;
    updateMTM() {
        const openQty = Math.round(this.buy_quantity - this.sell_quantity);
        if (openQty > 0) {
            this.mtm = parseFloat((Math.abs(openQty) * ((this.ltp / 100) - parseFloat(this.avg_buy_price))).toFixed(DEFAULT_PRECISION));
        } else {
            this.mtm = parseFloat((Math.abs(openQty) * (parseFloat(this.avg_sell_price) - (this.ltp / 100))).toFixed(DEFAULT_PRECISION));
        }

        this.net_pnl = (parseFloat(this.booked_pnl) + this.mtm || 0).toFixed(DEFAULT_PRECISION);

    }
}
